import { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { getJobs, downloadUrl } from '../api';
import StatusBadge from '../components/StatusBadge';
import '../styles/components/table.css';
import '../styles/components/buttons.css';

function formatDuration(start, end) {
  if (!start || !end) return '-';
  const secs = Math.floor((new Date(end) - new Date(start)) / 1000);
  if (secs < 60) return `${secs}s`;
  const m = Math.floor(secs / 60);
  const h = Math.floor(m / 60);
  if (h > 0) return `${h}h ${m % 60}m`;
  return `${m}m ${secs % 60}s`;
}

export default function JobHistoryPage() {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modeFilter, setModeFilter] = useState('all');
  const [fileFilter, setFileFilter] = useState('all');
  const navigate = useNavigate();
  const pollRef = useRef(null);

  useEffect(() => {
    let cancelled = false;

    const fetch = async () => {
      try {
        const data = await getJobs();
        if (!cancelled) {
          setJobs(data.filter(j => j.status !== 'cancelling'));
          setLoading(false);
        }
        const hasRunning = data.some(j => j.status === 'pending' || j.status === 'running' || j.status === 'cancelling');
        if (!hasRunning && pollRef.current) {
          clearInterval(pollRef.current);
          pollRef.current = null;
        }
      } catch {
        if (!cancelled) setLoading(false);
      }
    };

    fetch();
    pollRef.current = setInterval(fetch, 5000);

    return () => {
      cancelled = true;
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, []);

  const uniqueFiles = useMemo(() => {
    const seen = new Set();
    return jobs.reduce((acc, j) => {
      if (!seen.has(j.filename)) {
        seen.add(j.filename);
        acc.push(j.filename);
      }
      return acc;
    }, []);
  }, [jobs]);

  const filtered = useMemo(() => {
    return jobs.filter(j => {
      if (modeFilter !== 'all' && j.mode !== modeFilter) return false;
      if (fileFilter !== 'all' && j.filename !== fileFilter) return false;
      return true;
    });
  }, [jobs, modeFilter, fileFilter]);

  const latestPerFile = useMemo(() => {
    const map = {};
    for (const job of jobs) {
      if (job.status !== 'completed') continue;
      const prev = map[job.filename];
      if (!prev || new Date(job.completed_at) > new Date(prev.completed_at)) {
        map[job.filename] = job;
      }
    }
    return new Set(Object.values(map).map(j => j.id));
  }, [jobs]);

  const modeLabel = (m) => m === 'discovery' ? 'Discovery' : 'Age Est.';

  return (
    <div className="page-enter">
      <h2 style={{ fontSize: 24, fontWeight: 700, marginBottom: 6, letterSpacing: '-0.6px' }}>
        Job History
      </h2>
      <p style={{ fontSize: 14, color: 'var(--text3)', marginBottom: 20 }}>
        All submitted jobs
      </p>

      {!loading && jobs.length > 0 && (
        <div className="filter-bar">
          <div className="filter-group">
            <button
              className={`filter-btn${modeFilter === 'all' ? ' active' : ''}`}
              onClick={() => setModeFilter('all')}
            >All</button>
            <button
              className={`filter-btn${modeFilter === 'age_estimation' ? ' active' : ''}`}
              onClick={() => setModeFilter('age_estimation')}
            >Age Est.</button>
            <button
              className={`filter-btn${modeFilter === 'discovery' ? ' active' : ''}`}
              onClick={() => setModeFilter('discovery')}
            >Discovery</button>
          </div>
          {uniqueFiles.length > 1 && (
            <select
              className="filter-select"
              value={fileFilter}
              onChange={(e) => setFileFilter(e.target.value)}
            >
              <option value="all">All files</option>
              {uniqueFiles.map(f => (
                <option key={f} value={f}>{f}</option>
              ))}
            </select>
          )}
        </div>
      )}

      {loading ? (
        <div style={{ color: 'var(--text3)' }}>Loading...</div>
      ) : jobs.length === 0 ? (
        <div style={{ color: 'var(--text3)', textAlign: 'center', padding: 48 }}>
          No jobs yet
        </div>
      ) : (
        <div className="jobs-table-wrap">
          <table className="jobs-table">
            <thead>
              <tr>
                <th className="col-filename">Filename</th>
                <th className="col-mode">Mode</th>
                <th className="col-level">Lvl</th>
                <th className="col-rows">Rows</th>
                <th className="col-status">Status</th>
                <th className="col-duration">Duration</th>
                <th className="col-created">Created</th>
                <th className="col-download"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(job => {
                const isLatest = latestPerFile.has(job.id);
                return (
                  <tr key={job.id} onClick={() => navigate(`/jobs/${job.id}`)} style={job.superseded ? { opacity: 0.45 } : undefined}>
                    <td className="col-filename">{job.filename}</td>
                    <td className="col-mode">{modeLabel(job.mode)}</td>
                    <td className="col-level">{job.level || '-'}</td>
                    <td className="col-rows">{job.total_rows || '-'}</td>
                    <td className="col-status"><StatusBadge status={job.status} /></td>
                    <td className="col-duration">{formatDuration(job.started_at, job.completed_at)}</td>
                    <td className="col-created">
                      {job.created_at
                        ? <>
                            {new Date(job.created_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}{' '}
                            {new Date(job.created_at).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })}
                          </>
                        : '-'}
                    </td>
                    <td className="col-download">
                      {job.status === 'completed' && (
                        <a
                          href={downloadUrl(job.id)}
                          className={`download-link${isLatest ? ' latest' : ''}`}
                          onClick={(e) => e.stopPropagation()}
                          title={isLatest ? 'Latest results for this file' : 'Download'}
                        >
                          <svg width="20" height="20" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M8 2v8m0 0l-3-3m3 3l3-3M3 12h10"/>
                          </svg>
                        </a>
                      )}
                    </td>
                  </tr>
                );
              })}
              {filtered.length === 0 && (
                <tr>
                  <td colSpan="8" style={{ textAlign: 'center', color: 'var(--text3)', padding: 32 }}>
                    No jobs match filters
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
